import React, { useEffect, useRef, useState } from 'react';
import './OptionDropdown.scss';
import { useDropdownScrollLock, SCROLL_FEEDBACK_CLASS } from '../../DropdownScroll';
import { Input, Select } from 'antd';
import { ONBOARDING_STEPS } from '../../../../../utils/constants';
import { getText } from '../../../../../features/onboarding/components';
import { trackEvent, QuestionsAnsweredFromDropdown } from '../../../../../utils/mixpanel';
import { SearchOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';

interface OptionDropdownProps {
  values: any;
  setValue: (key: string, value: string) => void;
  index: number;
  isBrokerMode?: boolean;
  parentClassName?: string;
  isDisabled?: boolean;
  customWidth?: string;
}

export const OptionDropdown = (props: OptionDropdownProps) => {
  const { values, setValue, index, isBrokerMode, parentClassName, isDisabled, customWidth } = props;

  const stepText = ONBOARDING_STEPS[index].text
  const stepOptions = ONBOARDING_STEPS[index].options
  let stepCurrentValue = values[stepText]

  // Buscar la opción seleccionada, comparando de manera case-insensitive para manejar diferencias de mayúsculas/minúsculas
  const selectedOption = (stepOptions as any[]).find((op: any) => {
    if (op.text === stepCurrentValue) return true;
    // Comparación case-insensitive como fallback
    if (typeof op.text === 'string' && typeof stepCurrentValue === 'string') {
      return op.text.toLowerCase() === stepCurrentValue.toLowerCase();
    }
    return false;
  })
  
  // Asegurar que isSpecial se detecte correctamente incluso si selectedOption existe pero isSpecial es undefined
  // Verificar directamente en la opción encontrada y también buscar por texto si no se encontró
  let isSpecial = selectedOption?.isSpecial;
  
  // FIX: Para ficoRange (index 5), si el valor es "Less than 660" o "less than 660", 
  // forzar isSpecial=true independientemente de lo que diga la opción (ya que puede haber sido eliminado)
  if (index === 5 && stepCurrentValue) {
    const valueStr = String(stepCurrentValue).toLowerCase().trim();
    if (valueStr === 'less than 660' || valueStr.includes('less than 660')) {
      isSpecial = true;
    }
  }
  
  // Si no se encontró la opción o isSpecial es undefined (y no es el caso especial de index 5), intentar buscar de nuevo con diferentes variaciones
  if ((!selectedOption || isSpecial === undefined) && index !== 5) {
    // Buscar de nuevo con diferentes variaciones del texto
    const alternativeOption = (stepOptions as any[]).find((op: any) => {
      if (!stepCurrentValue || !op.text) return false;
      const opTextLower = String(op.text).toLowerCase().trim();
      const valueLower = String(stepCurrentValue).toLowerCase().trim();
      return opTextLower === valueLower || 
             opTextLower.includes(valueLower) || 
             valueLower.includes(opTextLower);
    });
    if (alternativeOption && alternativeOption.isSpecial !== undefined) {
      isSpecial = alternativeOption.isSpecial;
    }
  }

  let options = (
    stepOptions
      .map((option: any) => {
        const { text, label, displayTextDropdown, isDisabled, isSpecial } = option
        const normalizedLabel = getText(values, text, index)
        // Use displayTextDropdown for both label and displayLabel if available
        let finalLabel = displayTextDropdown || label || normalizedLabel || text
        // For loan purpose step (index 3), convert to lowercase for display
        if (index === 3) {
          finalLabel = finalLabel.toLowerCase()
        }
        return {
          value: text,
          label: finalLabel,
          displayLabel: finalLabel,
          disabled: isDisabled,
          className: isSpecial ? 'special' : undefined
        }
      })
  )

  if (index === 1) {
    if (!values[ONBOARDING_STEPS[2].text]) {
      stepCurrentValue = ""
      options = [
        {
          value: "",
          label: "bridge or rental",
          displayLabel: "bridge or rental",
          disabled: false,
          className: undefined
        }
      ]
    } else {
      const isBridge = (
        values[ONBOARDING_STEPS[1].text] === ONBOARDING_STEPS[1].options[0].text
        &&
        values[ONBOARDING_STEPS[2].text] === ONBOARDING_STEPS[2].options[0].text
      )
      const isRental = (
        values[ONBOARDING_STEPS[1].text] === ONBOARDING_STEPS[1].options[0].text
        &&
        values[ONBOARDING_STEPS[2].text] === ONBOARDING_STEPS[2].options[1].text
      )
      const isFix = (
        values[ONBOARDING_STEPS[1].text] === ONBOARDING_STEPS[1].options[1].text
        &&
        values[ONBOARDING_STEPS[2].text] === ONBOARDING_STEPS[2].options[0].text
      )

      if (isBridge) {
        stepCurrentValue = "bridge"
      } else if (isRental) {
        stepCurrentValue = "rental"
      } else if (isFix) {
        stepCurrentValue = "fix"
      }

      options = [
        {
          value: "bridge",
          label: "12-month bridge",
          displayLabel: "12-month bridge",
          disabled: false,
          className: undefined
        },
        {
          value: "fix",
          label: "12-month fix-and-flip",
          displayLabel: "12-month fix-and-flip",
          disabled: false,
          className: undefined
        },
        {
          value: "rental",
          label: "30-year rental",
          displayLabel: "30-year rental",
          disabled: false,
          className: undefined
        }
      ]
    }
  }

  const onChange = (value: string) => {
    if (index === 1) {
      if (value === "rental") {
        setValue(ONBOARDING_STEPS[2].text, ONBOARDING_STEPS[2].options[1].text);
        trackEvent(QuestionsAnsweredFromDropdown(ONBOARDING_STEPS[2].text, ONBOARDING_STEPS[2].options[1].text));
      } else if (value === "bridge") {
        setValue(ONBOARDING_STEPS[1].text, ONBOARDING_STEPS[1].options[0].text);
        trackEvent(QuestionsAnsweredFromDropdown(ONBOARDING_STEPS[1].text, ONBOARDING_STEPS[1].options[0].text));
      } else if (value === "fix") {
        setValue(ONBOARDING_STEPS[1].text, ONBOARDING_STEPS[1].options[1].text);
        trackEvent(QuestionsAnsweredFromDropdown(ONBOARDING_STEPS[1].text, ONBOARDING_STEPS[1].options[1].text));
      }
    } else {
      setValue(stepText, value);
      trackEvent(QuestionsAnsweredFromDropdown(stepText, value));
    }
  }

  const shouldDisable = (
    typeof isDisabled === 'boolean'
      ? isDisabled
      : (index === 1 && !values[ONBOARDING_STEPS[2].text])
  );

  // Si estamos en el caso especial donde solo debe mostrarse texto (index 1 sin valor en step 2)
  if (index === 1 && !values[ONBOARDING_STEPS[2].text]) {
    return (
      <span className="option no-underline " style={{ cursor: 'default' }}>bridge or rental</span>
    );
  }

  return (
    <OptionDropdownInner
      {
      ...{
        isDisabled: shouldDisable,
        isSpecial,
        isBrokerMode,
        defaultValue: stepCurrentValue,
        value: stepCurrentValue,
        options,
        onChange,
        parentClassName,
        customWidth
      }
      }
    />
  )
}

const ProcessingTeamWrapper = (
  onSearch: (value: string) => void,
  isLoadingMore?: boolean
) => {
  const WrapperComponent = (menu: React.ReactNode) => {
  return (
    <div>
      <div className='search'>
        <Input
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          placeholder='Search for a borrower'
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
      <div style={{ position: 'relative' }}>
        {menu}
        {isLoadingMore && (
          <div style={{
            textAlign: 'center',
            padding: '12px 8px',
            background: '#fafafa',
            borderTop: '1px solid #f0f0f0'
          }}>
            <span style={{ color: '#999', fontSize: '13px' }}>Loading more...</span>
          </div>
        )}
      </div>
    </div>
  );
  };
  WrapperComponent.displayName = 'ProcessingTeamWrapper';
  return WrapperComponent;
};

interface OptionDropdownInnerProps {
  isSpecial?: boolean;
  defaultValue?: string;
  value?: string;
  isBrokerMode?: boolean;
  options: any[];
  onChange: (value: string) => void;
  customWidth?: string;
  isDisabled?: boolean;
  isProcessingTeamMode?: boolean;
  onSearch?: (value: string) => void;
  parentClassName?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

export const OptionDropdownInner = (props: OptionDropdownInnerProps) => {
  const {
    isSpecial,
    defaultValue,
    value,
    isBrokerMode,
    options,
    onChange,
    customWidth,
    isDisabled,
    isProcessingTeamMode,
    onSearch,
    parentClassName,
    hasMore,
    onLoadMore,
    isLoadingMore
  } = props;

  const scrollListenerRef = useRef<(() => void) | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useDropdownScrollLock(isDropdownOpen);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(hasMore);

  // Mantener refs actualizados
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    if (!isLoadingMore) {
      isLoadingRef.current = false;
    }
  }, [isLoadingMore]);

  const processingClassNameFactory = (className: string) => (
    isProcessingTeamMode === true ? `${className} processing` : className
  )

  const specialClassNameFactory = (className: string) => (
    isSpecial === true ? `${className} special` : className
  )

  const brokerClassNameFactory = (className: string) => (
    isBrokerMode === true ? `${className} broker` : className
  )

  // Stable per instance: onOpenChange sets state (re-render), and the setTimeout below
  // must still find the popup by this class after that re-render.
  const customClassNameRef = useRef(`option-dropdown-${Math.trunc(Math.random() * 10000)}`)
  const customClassName = customClassNameRef.current

  const className = (
    `${brokerClassNameFactory(specialClassNameFactory(processingClassNameFactory(customClassName)))} ${SCROLL_FEEDBACK_CLASS}`
  )

  const onOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
    let DROPDOWN_WIDTH = customWidth || "300px";
    const MOBILE_BREAKPOINT = 780;

    if (window.innerWidth < MOBILE_BREAKPOINT) {
      DROPDOWN_WIDTH = "60vw"
    } else {
      DROPDOWN_WIDTH = customWidth || "300px"
    }

    setTimeout(() => {
      const elements = document.querySelectorAll(`.ant-select-dropdown.${customClassName}`)
      Array.from(elements).forEach((el) => {
        (el as HTMLElement).style.width = DROPDOWN_WIDTH;
        (el as HTMLElement).style.zIndex = '30000';
      })
    }, 0);

    // Agregar scroll listener cuando se abre el dropdown
    if (open && isProcessingTeamMode && onLoadMore) {
      setTimeout(() => {
        const scrollContainer = document.querySelector(
          `.ant-select-dropdown.${customClassName} .rc-virtual-list-holder`
        ) as HTMLElement;

        if (scrollContainer) {
          const handleScroll = debounce(() => {
            // Usar los refs para obtener valores actualizados
            if (!hasMoreRef.current || isLoadingRef.current) {
              return;
            }

            const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

            if (isNearBottom) {
              isLoadingRef.current = true;
              onLoadMore();
            }
          }, 200);

          scrollContainer.addEventListener('scroll', handleScroll);

          scrollListenerRef.current = () => {
            handleScroll.cancel();
            scrollContainer.removeEventListener('scroll', handleScroll);
            isLoadingRef.current = false;
          };
        }
      }, 100);
    } else if (!open && scrollListenerRef.current) {
      scrollListenerRef.current();
      scrollListenerRef.current = null;
    }
  }

  const wrapperClassName = [
    !isSpecial ? 'option' : 'option special',
    isDisabled ? 'disabled' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const MOBILE_BREAKPOINT = 780;
  const initialMinWidth = window.innerWidth < MOBILE_BREAKPOINT ? '60vw' : (customWidth || '300px');

  return (
    <span className={wrapperClassName} >
      <Select
        className={parentClassName}
        disabled={isDisabled}
        defaultValue={defaultValue}
        optionLabelProp='displayLabel'
        virtual={false}
        getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
        dropdownStyle={{
          position: 'fixed',
          minWidth: initialMinWidth
        }}
        dropdownAlign={{
          points: ['tl', 'bl'],
          offset: [0, 0]
        }}
        popupRender={
          isProcessingTeamMode && onSearch
            ? ProcessingTeamWrapper(onSearch, isLoadingMore)
            : undefined
        }
        notFoundContent={
          isProcessingTeamMode ? <div>No results found</div> : undefined
        }
        value={value}
        classNames={{ popup: { root: className } }}
        onOpenChange={onOpenChange}
        options={options}
        onChange={onChange}
      />
    </span>
  )
}